/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2025. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2025. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         21/03/25
 * Module name:  support-session-manager.service
 * File name:    support-session-manager.service.ts
 * IDE:          WebStorm
 */


import {computed, inject} from '@angular/core';
import {ZbSecureStorage} from '@zibanu/lib';
import {MANAGEMENT_STORAGE_KEY} from '@core/constants/management-storage-key';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {
  ManageSessionResponseInterface,
  ManageTypeEnum,
  VoyeurSessionDataInterface,
} from '@shared/interface/main/manage-session.interface';
import {MANAGE_SESSION_INIT_DATA} from '@core/constants/manage-session-init-data.const';
import {NO_INDEX_FIND} from '@shared/constants/general.constant';
import {MkadMenuManagerService, PreferencesService} from '@shared/services';
import {CustomerLevelEnum, CustomerParentsListPayload} from '@shared/interface';
import {Router} from '@angular/router';
import {BreadcrumbStore} from '@core/services/mkad-breadcrumb.store';
import {ManageSessionService} from '@auth/service/manage-session.service';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {switchMap, tap} from 'rxjs';
import {signalResponse} from '@core/parsers/signal-response';
import {MkadToastService} from '@core/services/mkad-toast.service';
import {UserLevelEnum, ZbAuthManagerService} from '@zibanu/auth';
import WINDOW from '@shared/utils/tokens';
import {OverviewStatsStore, OverviewStatsStoreType} from '@overview/store/overview-stats.store';

export interface ManagementSessionStoreInterface {
  loading: boolean;
  showConfirmation: boolean;
  storeData: ManageSessionResponseInterface;
}

export const ManagementSessionInitState: ManagementSessionStoreInterface = {
  loading: false,
  showConfirmation: false,
  storeData: MANAGE_SESSION_INIT_DATA,
};

export const ManageSessionStore = signalStore(
  {providedIn: 'root'},
  withState(ManagementSessionInitState),
  withComputed((store) => ({
    isLoading: computed(() => store.loading()),
    data: computed(() => store.storeData()),
    customerId: computed(() => store.storeData().customer_id),
    sessionToken: computed(() => store.storeData().manage_token),
    isManagementActive: computed(() => store.storeData().manage_type === ManageTypeEnum.support && store.storeData().customer_id > NO_INDEX_FIND),
    isVoyeurActive: computed(() => store.storeData().manage_type === ManageTypeEnum.voyeur && store.storeData().customer_id > NO_INDEX_FIND),
  })),
  withMethods((
      store,
      router: Router = inject(Router),
      menuService: MkadMenuManagerService = inject(MkadMenuManagerService),
      breadcrumbService = inject(BreadcrumbStore),
      manageService = inject(ManageSessionService),
      toast: MkadToastService = inject(MkadToastService),
      statsStore: OverviewStatsStoreType = inject(OverviewStatsStore),
    ) => {
      const storage = new ZbSecureStorage();
      // todo validar uso del main store en lugar del management store en el guard
      const authService: ZbAuthManagerService = inject(ZbAuthManagerService);
      const preferences: PreferencesService = inject(PreferencesService);
      const windows: Window = inject(WINDOW);

      const authServiceRef = () => authService;
      const preferencesRef = () => preferences;

      function setData(data: ManageSessionResponseInterface): void {
        storage.setItem(MANAGEMENT_STORAGE_KEY, JSON.stringify(data));
        patchState(store, {storeData: data, loading: false});
      }

      function setVoyeurData(data: Omit<VoyeurSessionDataInterface, 'manage_type'>): void {
        const voyeurData: ManageSessionResponseInterface = {
          ...data,
          manage_type: ManageTypeEnum.voyeur,
        };
        setData(voyeurData);
        menuService.reloadMenuForVoyeur(
          voyeurData.customer_level === CustomerLevelEnum.final
        );
        router.navigate(['overview']).then();
      }

      function setSupportData(data: Omit<ManageSessionResponseInterface, 'manage_type'>): void {
        const supportData: ManageSessionResponseInterface = {
          ...data,
          manage_type: ManageTypeEnum.support,
        };
        setData(supportData);
        router.navigate(['overview']).then();
      }

      function validateStatsReload(): void {
        if (router.url === '/overview') {
          const params: CustomerParentsListPayload | undefined = store.isVoyeurActive()
            ? {customer: store.storeData().customer_id}
            : undefined;
          statsStore.getStats(params);
        }
      }

      function removeData(): void {
        storage.removeItem(MANAGEMENT_STORAGE_KEY);
        breadcrumbService.cleanStoreData();
        patchState(store, {storeData: MANAGE_SESSION_INIT_DATA});
        const customerLevel = preferences.userCustomer()?.customer_level as CustomerLevelEnum;
        const route = authService.isAuthenticated()
          ? customerLevel === CustomerLevelEnum.final ? 'home' : 'overview'
          : 'home';
        router.navigate([route]).then();
      }

      function endSessionResponse(): void {
        removeData();
        validateStatsReload();
        const customerLevel = preferences.userCustomer()?.customer_level;
        if (authService.isAuthenticated()) {
          const clientLevel = (customerLevel === undefined || customerLevel === CustomerLevelEnum.final) ? CustomerLevelEnum.final : CustomerLevelEnum.silver;
          const diamondLevel = preferences.userCustomer()?.customer_level === CustomerLevelEnum.diamond;
          menuService.reloadMainMenu(preferences.level, clientLevel, diamondLevel);
        } else {
          menuService.reloadMainMenu(UserLevelEnum.Guest, CustomerLevelEnum.final);
        }
        if (!preferences.userCustomer()?.allow_customers) {
          menuService.removeContractInMenu();
        }
      }

      const handleBeforeUnloadEvent = (event: BeforeUnloadEvent) => {
        if (store.showConfirmation()) {
          event.preventDefault();
          event.returnValue = 'true';
          return '';
        }
        return;
      };

      const startSessionManage = rxMethod<void>(($) => $.pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap(() => manageService.startManage(store.data().customer_id).pipe(
          signalResponse(
            (response) => {
              patchState(store, {showConfirmation: true});
              windows.addEventListener('beforeunload', handleBeforeUnloadEvent);
              setSupportData({
                customer_id: response.customer_id,
                customer_level: response.customer_level,
                customer_name: store.data().customer_name,
                customer_initials: store.data().customer_name,
                customer_enabled: response.customer_enabled,
                allow_customers: response.allow_customers,
                allow_public_devs: response.allow_public_devs,
                user_level: response.user_level,
                manage_token: response.manage_token,
              });
              menuService.reloadMainMenu(
                store.data().user_level,
                store.data().customer_level
              );
              if (!store.data().allow_customers) {
                menuService.removeContractInMenu();
              }

            },
            (error) => toast.showError(error.message),
          ),
        )),
      ));

      const endSessionManage = rxMethod<void>(($) => $.pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap(() => manageService.endManage().pipe(
          signalResponse(
            () => {
              patchState(store, {showConfirmation: false});
              endSessionResponse();
            },
            (error) => {
              toast.showError(error.message);
              removeData();
              validateStatsReload();
            },
          ),
        )),
      ));

      return {
        removeData,
        setVoyeurData,
        setSupportData,
        startSessionManage,
        endSessionManage,
        endSessionResponse,
        authServiceRef,
        preferencesRef,
      };
    },
  ),
  withHooks((store) => ({
    onInit: () => {
      const storage = new ZbSecureStorage();
      const storeData: string | null = storage.getItem(MANAGEMENT_STORAGE_KEY);
      if (storeData === null) {
        patchState(store, {storeData: MANAGE_SESSION_INIT_DATA, showConfirmation: false});
      } else {
        patchState(store, {storeData: JSON.parse(storeData), showConfirmation: true});
      }
    },
  })),
);

export type ManageSessionStoreType = InstanceType<typeof ManageSessionStore>;
